import { AnglesUpDown } from "@/icons/AnglesUpDown.tsx";
import { CircleInfo } from "@/icons/CircleInfo.tsx";
import { Clone } from "@/icons/Clone.tsx";
import { Download } from "@/icons/Download.tsx";
import { FolderImage } from "@/icons/FolderImage.tsx";
import { Heart } from "@/icons/Heart.tsx";
import { HeartFilled } from "@/icons/HeartFilled.tsx";
import { MicrochipAI } from "@/icons/MicrochipAI.tsx";
import { PenToSquare } from "@/icons/PenToSquare";
import { Share } from "@/icons/Share.tsx";
import { ShareNodes } from "@/icons/ShareNodes.tsx";
import { Star } from "@/icons/Star.tsx";
import { StarFilled } from "@/icons/StarFilled.tsx";
import { TrashCan } from "@/icons/TrashCan.tsx";
import api from "@/lib/api.ts";
import {
  copyToClipboard,
  getPhotoDownloadUrl,
  handleDownload,
  photoStyleColorMap,
  photoStyleMap,
} from "@/lib/helpers.ts";
import { PhotoModel } from "@/models/photo.model.ts";
import { TagsResponse } from "@/models/tag.model.ts";
import useUserStore from "@/store/user.store.ts";
import { LoadingImage } from "@/ui/shared/LoadingImage.tsx";
import { EmailShareButton } from "@/ui/shared/social/EmailShareButton.tsx";
import { ViberShareButton } from "@/ui/shared/social/ViberShareButton.tsx";
import { WhatsAppShareButton } from "@/ui/shared/social/WhatsAppShareButton.tsx";
import { XShareButton } from "@/ui/shared/social/XShareButton.tsx";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Flex,
  Group,
  LoadingOverlay,
  Menu,
  Modal,
  Paper,
  Popover,
  SegmentedControl,
  TagsInput,
  Text,
  TextInput,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure, useViewportSize } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useMutation } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useState, useMemo, memo, useEffect } from "react";
import { SubmitHandler, useForm, UseFormReturn } from "react-hook-form";
import { Link } from "react-router";
import { z } from "zod";

type PhotoCardProps = {
  photo: PhotoModel;
  onClick?: () => void;
  onDelete?: () => void;
  form?: UseFormReturn<{
    prompt: string;
  }>;
  width?: number;
  showApp?: boolean;
  showAI?: boolean;
  showOptions?: boolean;
  onPhotoCopySettings?: (photo: PhotoModel) => void;
};

type TemplateForm = {
  name: string;
  type: "text" | "image";
  photo_id: number | null;
  tags: string[];
};

export const PhotoCard = memo(function PhotoCard(props: PhotoCardProps) {
  const templateForm = useForm<TemplateForm>({
    resolver: zodResolver(
      z.object({
        photo_id: z.number().nullable().optional(),
        type: z.enum(["text", "image"]),
        name: z.string().min(1, { message: "Name is required" }),
        tags: z.array(z.string()).optional().default([]),
      }),
    ),
    defaultValues: {
      name: "",
      type: "text",
      photo_id: null,
      tags: [],
    },
  });
  const theme = useMantineTheme();
  const { isAdmin } = useUserStore();
  const {
    photo,
    onDelete,
    onClick,
    onPhotoCopySettings,
    form,
    width = 400,
    showApp = false,
    showAI = true,
    showOptions = true,
  } = props;
  const { width: viewportWidth } = useViewportSize();
  const [mutatingPhotoId, setMutatingPhotoId] = useState<null | number>(null);
  const [
    confirmationOpened,
    { open: openConfirmation, close: closeConfirmation },
  ] = useDisclosure(false);
  const [templateOpened, { open: openTemplate, close: closeTemplate }] =
    useDisclosure(false);
  const [tags, setTags] = useState<string[]>([]);
  const minWidth = width < viewportWidth - 30 ? width : viewportWidth - 36;

  const photoDeleteMutation = useMutation({
    mutationFn: (photoId: number) => {
      return api.delete(`/photos/${photoId}`);
    },
    onSuccess: () => {
      setMutatingPhotoId(null);

      if (onDelete) {
        onDelete();
      }
    },
  });

  const templateMutation = useMutation({
    mutationFn: (templateForm: TemplateForm) => {
      templateForm.photo_id = photo.id;

      return api.post("/templates", templateForm);
    },
    onSuccess: () => {
      closeTemplate();
      templateForm.reset();

      notifications.show({
        title: "Success!",
        message: "Your template has been successfully created.",
        color: "green",
      });
    },
  });

  const onTemplateSubmit: SubmitHandler<TemplateForm> = (data) => {
    templateMutation.mutate(data);
  };

  const coverMutation = useMutation({
    mutationFn: (photo: PhotoModel) => {
      return api.post(`/photos/${photo.id}/cover`, {});
    },
    onSuccess: () => {
      notifications.show({
        title: "Success!",
        message: "Your character's cover has been successfully updated.",
        color: "green",
      });
    },
  });

  const photoStarMutation = useMutation({
    mutationFn: (name: string) => {
      return api.post(`/photos/${name}/star`);
    },
  });

  const photoLikeMutation = useMutation({
    mutationFn: (name: string) => {
      return api.post(`/photos/${name}/like`);
    },
  });

  const handleCaptionMutation = () => {
    captionMutation.mutate();
  };

  const captionMutation = useMutation<AxiosResponse<{ prompt: string }>>({
    mutationFn: () => {
      return api.post("/prompts/caption", {
        input_photo_id: photo.id,
      });
    },
  });

  useEffect(() => {
    if (captionMutation.data?.data.prompt) {
      form?.setValue("prompt", captionMutation.data.data.prompt);
    }
  }, [captionMutation.data]);

  useEffect(() => {
    if (templateOpened) {
      api
        .get("/tags?limit=100")
        .then((response: AxiosResponse<TagsResponse>) => {
          setTags(response.data.data.map((t) => t.name));
        });
    }
  }, [templateOpened]);

  const onPhotoDelete = (photo: PhotoModel) => {
    openConfirmation();
    setMutatingPhotoId(photo.id);
  };

  const starTooltip = useMemo(
    () => (
      <Tooltip label="Star photo" withArrow>
        <ActionIcon
          onClick={() => {
            photoStarMutation.mutate(photo.name);
            photo.starred = !photo.starred;
          }}
          size="lg"
          className="opacity-65"
          variant="default"
        >
          {photo.starred && (
            <StarFilled style={{ fill: theme.colors.yellow[6] }} />
          )}
          {!photo.starred && <Star style={{ fill: theme.colors.yellow[6] }} />}
        </ActionIcon>
      </Tooltip>
    ),
    [photoStarMutation, photo.name, photo.starred, theme.colors.yellow],
  );

  const likeTooltip = useMemo(
    () => (
      <Tooltip label="Like photo" withArrow>
        <ActionIcon
          onClick={() => {
            photoLikeMutation.mutate(photo.name);
            photo.liked = !photo.liked;
          }}
          size="lg"
          className="opacity-65"
          variant="default"
        >
          {photo.liked && <HeartFilled style={{ fill: theme.colors.red[6] }} />}
          {!photo.liked && <Heart style={{ fill: theme.colors.red[6] }} />}
        </ActionIcon>
      </Tooltip>
    ),
    [photoLikeMutation, photo.name, photo.liked, theme.colors.red],
  );

  const share = useMemo(
    () => (
      <Popover withArrow>
        <Popover.Target>
          <Tooltip label="Share photo" withArrow>
            <ActionIcon
              disabled={captionMutation.isPending}
              loading={captionMutation.isPending}
              loaderProps={{ type: "dots" }}
              size="lg"
              className="opacity-65"
              variant="default"
            >
              <Share />
            </ActionIcon>
          </Tooltip>
        </Popover.Target>

        <Popover.Dropdown className="flex ">
          <div className="inline-flex gap-2">
            <EmailShareButton
              url={photo.url}
              subject="PhotoFuseAI Photo"
              body="Here is my PhotoFuseAI photo:"
            />

            <ViberShareButton
              url={photo.url}
              text="Here is my PhotoFuseAI photo:"
            />

            <WhatsAppShareButton
              url={photo.url}
              text="Here is my PhotoFuseAI photo:"
            />

            <XShareButton
              url={photo.url}
              text="Here is my PhotoFuseAI photo:"
            />
          </div>
        </Popover.Dropdown>
      </Popover>
    ),
    [],
  );

  const options = useMemo(
    () => (
      <Menu shadow="md">
        <Menu.Target>
          <Tooltip label="Options" withArrow>
            <ActionIcon
              disabled={captionMutation.isPending}
              loading={captionMutation.isPending}
              loaderProps={{ type: "dots" }}
              size="lg"
              className="opacity-65"
              variant="default"
            >
              <AnglesUpDown style={{ fill: theme.colors.violet[6] }} />
            </ActionIcon>
          </Tooltip>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Label>Actions</Menu.Label>
          <Menu.Item
            component={Link}
            to={`/editor/${photo.name}`}
            leftSection={<PenToSquare />}
          >
            Edit
          </Menu.Item>
          <Menu.Item
            onClick={() => onPhotoDelete(photo)}
            leftSection={<TrashCan />}
          >
            Delete
          </Menu.Item>
          <Menu.Item
            onClick={() =>
              handleDownload(getPhotoDownloadUrl(photo), photo.name)
            }
            leftSection={<Download />}
          >
            Download
          </Menu.Item>
          <Menu.Item
            onClick={() => {
              copyToClipboard(photo.url);
              notifications.show({
                title: "Success!",
                message: "Link copied to clipboard!",
                color: "green",
              });
            }}
            leftSection={<ShareNodes />}
          >
            Copy link
          </Menu.Item>
          <Menu.Item
            onClick={() => coverMutation.mutate(photo)}
            leftSection={<FolderImage />}
          >
            Set as cover
          </Menu.Item>
          {showAI && (
            <>
              <Menu.Label>Extract photo settings</Menu.Label>
              {photo.prompt && (
                <Menu.Item
                  onClick={() => form?.setValue("prompt", photo.prompt)}
                  leftSection={<Clone />}
                >
                  Copy prompt
                </Menu.Item>
              )}
              {onPhotoCopySettings && (
                <Menu.Item
                  onClick={() => onPhotoCopySettings(photo)}
                  leftSection={<Clone />}
                >
                  Copy settings
                </Menu.Item>
              )}
              <Menu.Item
                onClick={handleCaptionMutation}
                leftSection={<MicrochipAI />}
              >
                Generate
              </Menu.Item>
            </>
          )}
          {isAdmin && (
            <>
              <Menu.Label>Admin settings</Menu.Label>

              <Menu.Item onClick={openTemplate} leftSection={<FolderImage />}>
                Create template
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>
    ),
    [
      captionMutation.isPending,
      theme.colors.violet,
      photo.name,
      photo.prompt,
      showAI,
      onPhotoDelete,
      form,
      handleCaptionMutation,
    ],
  );

  const styleBadge = useMemo(
    () => (
      <Badge
        color={photoStyleColorMap[photo.style]}
        radius="md"
        className="absolute bottom-2 left-2"
        variant="dot"
      >
        {photoStyleMap[photo.style]}
      </Badge>
    ),
    [photo.style],
  );

  const loraBadge = useMemo(
    () =>
      photo.lora.data ? (
        <Badge radius="md" variant="dot">
          {photo.lora.data.name}
        </Badge>
      ) : null,
    [photo.lora.data],
  );

  return (
    <>
      <LoadingOverlay
        visible={mutatingPhotoId === photo.id}
        loaderProps={{ color: "pink", type: "bars" }}
        zIndex={5}
        overlayProps={{ radius: "sm", blur: 0.5 }}
      />

      <LoadingImage
        onClick={onClick}
        radius="md"
        w={minWidth}
        // h="auto"
        h={minWidth}
        // mah={minWidth}
        src={photo.url}
      />

      {showOptions && (
        <div className="absolute top-2 left-2 flex gap-1">
          {showApp && (
            <Badge radius="md" variant="dot">
              {photo.app}
            </Badge>
          )}

          {loraBadge}
        </div>
      )}

      {showOptions && photo.style && styleBadge}

      {showOptions && (
        <div className="absolute top-2 right-2 flex items-center gap-1">
          {starTooltip}
          {likeTooltip}
        </div>
      )}

      {showOptions && (
        <div className="absolute bottom-2 right-2 flex items-center gap-1">
          {share}
          {options}
        </div>
      )}

      <Modal
        opened={confirmationOpened}
        onClose={() => {
          closeConfirmation();
          setMutatingPhotoId(null);
        }}
        title="Are you sure?"
      >
        <div className="flex flex-col gap-4">
          <Alert icon={<CircleInfo size={20} />} color="red">
            This action cannot be undone. Are you sure you want to delete this
            photo?
          </Alert>

          <Group justify="end">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => {
                setMutatingPhotoId(null);
                closeConfirmation();
              }}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => {
                if (mutatingPhotoId) {
                  photoDeleteMutation.mutate(mutatingPhotoId);
                }
                closeConfirmation();
              }}
            >
              Delete
            </Button>
          </Group>
        </div>
      </Modal>

      <Modal
        opened={templateOpened}
        onClose={() => {
          templateForm.reset();
          closeTemplate();
        }}
        title="Create photo template"
      >
        <form
          onSubmit={templateForm.handleSubmit(onTemplateSubmit)}
          className="flex flex-col gap-4"
        >
          <Alert
            bd="1px dashed violet.6"
            icon={<CircleInfo size={20} />}
            color="violet"
          >
            Create a template using this photo as a source.
          </Alert>

          <div className="flex flex-col gap-4">
            <Paper withBorder={true} className="overflow-hidden">
              <img src={photo!.url} alt="Photo" />
            </Paper>

            <TextInput
              {...templateForm.register("name")}
              type="text"
              name="name"
              withAsterisk
              label="Name"
              size="md"
              error={templateForm.formState.errors.name?.message}
            />

            <TagsInput
              defaultValue={templateForm.getValues("tags")}
              onChange={(value) => templateForm.setValue("tags", value)}
              label="Tags"
              size="md"
              name="tags"
              data={tags}
            />

            <Flex direction="column">
              <Text fw={500} component="label" mb={0}>
                Type
              </Text>

              <SegmentedControl
                defaultValue={templateForm.getValues("type")}
                onChange={(value) =>
                  templateForm.setValue("type", value as "text" | "image")
                }
                data={[
                  {
                    label: "Text",
                    value: "text",
                  },
                  {
                    label: "Image",
                    value: "image",
                  },
                ]}
              />
            </Flex>
          </div>

          <Group justify="end">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => {
                closeTemplate();
                templateForm.reset();
              }}
            >
              Cancel
            </Button>
            <Button
              loading={templateMutation.isPending}
              disabled={templateMutation.isPending}
              type="submit"
            >
              Create
            </Button>
          </Group>
        </form>
      </Modal>
    </>
  );
});
